import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { BROWSE_MAX_QUERY_LENGTH } from 'fym-shared/src/music24/browseSearch';
import { TypedMemo } from '../../../common/types';
import { browseMessages as messages } from '../browseMessages';
import { useSlashFocus } from '../hooks/useSlashFocus';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseSearch.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The pause after the last key before the page searches (design a1 note: 300 ms). */
export const BROWSE_SEARCH_DEBOUNCE_MS = 300;

interface BrowseSearchBoxProps {
  /** The query in the URL. A change from outside (a Try chip, a recent search) fills the box. */
  query: string;
  onQueryChange: (query: string) => void;
  /** Enter: search at once and keep the query in the recent searches. */
  onSubmit: (query: string) => void;
}

/**
 * The big search box (design a1). The box keeps its own text and writes the URL 300 ms after the
 * last key, so typing never waits for the router. "/" focuses it.
 */
const BrowseSearchBoxComponent: React.FC<BrowseSearchBoxProps> = ({ query, onQueryChange, onSubmit }) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(query);
  const lastSent = useRef(query);
  useSlashFocus(inputRef);

  useEffect(() => {
    if (query !== lastSent.current) {
      lastSent.current = query;
      setText(query);
    }
  }, [query]);

  // The latest callback, so the debounce lives as long as the box and a new callback never drops a key.
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  const send = useMemo(
    () =>
      debounce((value: string) => {
        lastSent.current = value;
        onQueryChangeRef.current(value);
      }, BROWSE_SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => send.cancel(), [send]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
      send(event.target.value);
    },
    [send]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        send.cancel();
        lastSent.current = text;
        onQueryChange(text);
        onSubmit(text);
      }
    },
    [send, text, onQueryChange, onSubmit]
  );

  const clear = useCallback(() => {
    send.cancel();
    setText('');
    lastSent.current = '';
    onQueryChange('');
    inputRef.current?.focus();
  }, [send, onQueryChange]);

  return (
    <div className={styles.box}>
      <FontAwesomeIcon icon={faMagnifyingGlass} />
      <input
        ref={inputRef}
        className={styles.input}
        type="search"
        value={text}
        maxLength={BROWSE_MAX_QUERY_LENGTH}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={intl.formatMessage(messages.placeholder)}
        aria-label={intl.formatMessage(messages.searchTitle)}
        data-testid="browse-search-input"
      />
      {text !== '' && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={clear}
          aria-label={intl.formatMessage(messages.clearQuery)}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
      <span className={styles.kbd} aria-hidden="true">
        /
      </span>
    </div>
  );
};

export const BrowseSearchBox = TypedMemo(BrowseSearchBoxComponent);
